import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Building, MapPin, Clock, Briefcase, DollarSign, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { SEO, getJobPostingSchema, getBreadcrumbSchema } from '../components/SEO';

const API = '/api';

export const JobDetailPage = () => {
  const { t, language, direction } = useLanguage();
  const { jobId } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchJob();
  }, [jobId]);

  const fetchJob = async () => {
    try {
      const response = await fetch(`${API}/jobs/${jobId}`);
      if (response.ok) {
        const data = await response.json();
        setJob(data);
      }
    } catch (error) {
      console.error('Error fetching job:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#33AADD]"></div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-[#111827] mb-4">Job not found</h2>
          <Link to="/jobs">
            <Button className="btn-primary">Back to Jobs</Button>
          </Link>
        </div>
      </div>
    );
  }

  const jobTitle = language === 'ar' && job.title_ar ? job.title_ar : job.title;
  const jobDescription = language === 'ar' && job.description_ar ? job.description_ar : job.description;
  const jobLocation = language === 'ar' && job.location_ar ? job.location_ar : job.location;
  const jobCountry = language === 'ar' && job.country_ar ? job.country_ar : job.country;

  const jobSchema = getJobPostingSchema(job, language);
  const breadcrumbSchema = getBreadcrumbSchema([
    { name: language === 'ar' ? 'الرئيسية' : 'Home', url: '/' },
    { name: language === 'ar' ? 'الوظائف' : 'Jobs', url: '/jobs' },
    { name: jobTitle }
  ]);

  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="job-detail-page">
      <SEO
        title={`${job.title} - ${job.company}`}
        titleAr={job.title_ar ? `${job.title_ar} - ${job.company}` : undefined}
        description={`${job.title} position at ${job.company} in ${job.location}, ${job.country}. ${job.employment_type} role. ${jobDescription?.slice(0, 150)}...`}
        descriptionAr={job.description_ar ? `وظيفة ${job.title_ar} في ${job.company} في ${job.location_ar || job.location}. ${job.description_ar?.slice(0, 150)}...` : undefined}
        keywords={`${job.title}, ${job.company}, ${job.location}, ${job.country}, jobs, careers, ${job.skills?.join(', ') || ''}`}
        canonicalUrl={`/jobs/${job.job_id}`}
        structuredData={[jobSchema, breadcrumbSchema]}
        language={language}
      />
      {/* Header */}
      <section className="bg-white border-b border-[#E5E7EB] py-8">
        <div className="container-custom">
          <Link 
            to="/jobs" 
            className="inline-flex items-center text-[#6B7280] hover:text-[#33AADD] mb-6"
            data-testid="back-to-jobs"
          >
            <ArrowLeft className={`h-4 w-4 me-2 ${direction === 'rtl' ? 'rtl-flip' : ''}`} />
            {t('common.back')} {t('nav.jobs')}
          </Link>
          
          <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-6">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 rounded-xl bg-[#33AADD]/10 flex items-center justify-center flex-shrink-0">
                <Building className="h-8 w-8 text-[#33AADD]" />
              </div>
              <div>
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <h1 className="text-2xl sm:text-3xl font-bold text-[#111827]">
                    {language === 'ar' && job.title_ar ? job.title_ar : job.title}
                  </h1>
                  {job.is_featured && (
                    <Badge className="bg-[#33AADD] text-white">Featured</Badge>
                  )}
                </div>
                <p className="text-lg text-[#6B7280] mb-3">{job.company}</p>
                <div className="flex flex-wrap items-center gap-4 text-sm text-[#6B7280]">
                  <span className="flex items-center gap-1">
                    <MapPin className="h-4 w-4" />
                    {language === 'ar' && job.location_ar ? job.location_ar : job.location}, {language === 'ar' && job.country_ar ? job.country_ar : job.country}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    {job.employment_type === 'full-time' ? t('jobs.fullTime') :
                     job.employment_type === 'part-time' ? t('jobs.partTime') :
                     job.employment_type === 'contract' ? t('jobs.contract') : t('jobs.internship')}
                  </span>
                  <Badge variant="outline">
                    {job.work_model === 'onsite' ? t('jobs.onsite') :
                     job.work_model === 'hybrid' ? t('jobs.hybrid') : t('jobs.remote')}
                  </Badge>
                </div>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to={`/jobs/${job.job_id}/apply`}>
                <Button size="lg" className="btn-primary w-full sm:w-auto" data-testid="apply-now-btn">
                  {t('jobs.apply')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Description */}
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-xl font-semibold text-[#111827] mb-4">
                    {language === 'ar' ? 'وصف الوظيفة' : 'Job Description'}
                  </h2>
                  <p className="text-[#6B7280] whitespace-pre-line">
                    {language === 'ar' && job.description_ar ? job.description_ar : job.description}
                  </p>
                </CardContent>
              </Card>

              {/* Responsibilities */}
              {((language === 'ar' && job.responsibilities_ar?.length > 0) || job.responsibilities?.length > 0) && (
                <Card>
                  <CardContent className="p-6">
                    <h2 className="text-xl font-semibold text-[#111827] mb-4">
                      {language === 'ar' ? 'المسؤوليات' : 'Responsibilities'}
                    </h2>
                    <ul className="space-y-3">
                      {(language === 'ar' && job.responsibilities_ar?.length > 0 
                        ? job.responsibilities_ar 
                        : job.responsibilities || []
                      ).map((item, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <CheckCircle2 className="h-5 w-5 text-[#33AADD] flex-shrink-0 mt-0.5" />
                          <span className="text-[#6B7280]">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

              {/* Requirements */}
              {((language === 'ar' && job.requirements_ar?.length > 0) || job.requirements?.length > 0) && (
                <Card>
                  <CardContent className="p-6">
                    <h2 className="text-xl font-semibold text-[#111827] mb-4">{t('jobs.requirements')}</h2>
                    <ul className="space-y-3">
                      {(language === 'ar' && job.requirements_ar?.length > 0 
                        ? job.requirements_ar 
                        : job.requirements || []
                      ).map((req, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <CheckCircle2 className="h-5 w-5 text-[#33AADD] flex-shrink-0 mt-0.5" />
                          <span className="text-[#6B7280]">{req}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

              {/* Benefits */}
              {((language === 'ar' && job.benefits_ar?.length > 0) || job.benefits?.length > 0) && (
                <Card>
                  <CardContent className="p-6">
                    <h2 className="text-xl font-semibold text-[#111827] mb-4">{t('jobs.benefits')}</h2>
                    <ul className="space-y-3">
                      {(language === 'ar' && job.benefits_ar?.length > 0 
                        ? job.benefits_ar 
                        : job.benefits || []
                      ).map((benefit, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-[#6B7280]">{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Job Overview */}
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-[#111827] mb-4">Job Overview</h3>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#F9FAFB] flex items-center justify-center">
                        <Briefcase className="h-5 w-5 text-[#33AADD]" />
                      </div>
                      <div>
                        <p className="text-sm text-[#6B7280]">{t('forms.employmentType')}</p>
                        <p className="font-medium text-[#111827]">
                          {job.employment_type === 'full-time' ? t('jobs.fullTime') :
                           job.employment_type === 'part-time' ? t('jobs.partTime') :
                           job.employment_type === 'contract' ? t('jobs.contract') : t('jobs.internship')}
                        </p>
                      </div>
                    </div>
                    
                    <Separator />
                    
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#F9FAFB] flex items-center justify-center">
                        <MapPin className="h-5 w-5 text-[#33AADD]" />
                      </div>
                      <div>
                        <p className="text-sm text-[#6B7280]">{t('forms.workModel')}</p>
                        <p className="font-medium text-[#111827]">
                          {job.work_model === 'onsite' ? t('jobs.onsite') :
                           job.work_model === 'hybrid' ? t('jobs.hybrid') : t('jobs.remote')}
                        </p>
                      </div>
                    </div>
                    
                    {job.experience_years && (
                      <>
                        <Separator />
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-[#F9FAFB] flex items-center justify-center">
                            <Clock className="h-5 w-5 text-[#33AADD]" />
                          </div>
                          <div>
                            <p className="text-sm text-[#6B7280]">{t('jobs.experience')}</p>
                            <p className="font-medium text-[#111827]">
                              {job.experience_years}+ {t('jobs.years')}
                            </p>
                          </div>
                        </div>
                      </>
                    )}
                    
                    {job.salary_max && (
                      <>
                        <Separator />
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-[#F9FAFB] flex items-center justify-center">
                            <DollarSign className="h-5 w-5 text-[#33AADD]" />
                          </div>
                          <div>
                            <p className="text-sm text-[#6B7280]">{t('jobs.salary')}</p>
                            <p className="font-medium text-[#111827]">
                              {job.currency} {job.salary_min?.toLocaleString()} - {job.salary_max?.toLocaleString()}
                            </p>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Skills */}
              {((language === 'ar' && job.skills_ar?.length > 0) || job.skills?.length > 0) && (
                <Card>
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold text-[#111827] mb-4">{t('jobs.skills')}</h3>
                    <div className="flex flex-wrap gap-2">
                      {(language === 'ar' && job.skills_ar?.length > 0 
                        ? job.skills_ar 
                        : job.skills || []
                      ).map((skill, index) => (
                        <Badge key={index} variant="secondary">
                          {skill}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Apply CTA */}
              <Card className="bg-[#33AADD] border-none">
                <CardContent className="p-6 text-center">
                  <h3 className="text-lg font-semibold text-white mb-2">Interested in this role?</h3>
                  <p className="text-white/90 text-sm mb-4">Apply now and join our team!</p>
                  <Link to={`/jobs/${job.job_id}/apply`}>
                    <Button variant="secondary" className="w-full bg-white text-[#33AADD] hover:bg-white/90" data-testid="sidebar-apply-btn">
                      {t('jobs.apply')}
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default JobDetailPage;
